/**
 * scrollUtils.js — รีเซ็ต scroll position ของทุก element ที่ scroll ได้ในหน้า
 *
 * ปัญหา: ในแอปมี scroll container หลายชั้น (window, html, body,
 * #main-scroll-container, <main>, และ .overflow-y-auto ต่างๆ ใน sub-component)
 * การ reset แค่บางตัวไม่เพียงพอ — ต้อง loop หาทุกตัวที่ scrollTop > 0
 *
 * เรียกใช้:
 *   resetAllScrollPositions()  // reset ทันที
 *   scheduleScrollReset(1500) // reset ซ้ำๆ เป็นเวลา 1.5s (กัน async content)
 */

const SCROLL_SELECTOR =
  "#main-scroll-container, main, .overflow-y-auto, .overflow-auto, [data-scroll-container]";


export const resetAllScrollPositions = () => {
  // 1. Reset window + html + body
  window.scrollTo(0, 0);
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;

  // 2. Reset every scrollable element on the page
  document.querySelectorAll(SCROLL_SELECTOR).forEach((el) => {
    el.scrollTop = 0;
  });

  // 3. Catch-all: any element that has scrollTop > 0 right now
  document.querySelectorAll("*").forEach((el) => {
    try {
      if (el.scrollTop && el.scrollTop > 0) el.scrollTop = 0;
    } catch {
      /* ignore read-only elements */
    }
  });
};

/**
 * Reset scroll immediately, then again on each rAF for `duration` ms
 * to beat any async content (images, fetch, lazy chunks) that may
 * re-scroll the page after first paint.
 *
 * Also overrides `scroll-behavior: smooth` for the duration by setting
 * inline `scroll-behavior: auto` on html, body, and known containers.
 *
 * IMPORTANT: If the user starts scrolling (wheel/touch/keydown) we stop
 * the loop immediately so we don't fight their input.
 *
 * Returns a cleanup function that stops the rAF loop early and restores
 * original scroll-behavior styles.
 */
export const scheduleScrollReset = (durationMs = 250) => {
  const html = document.documentElement;
  const body = document.body;
  const prevHtml = html.style.scrollBehavior;
  const prevBody = body.style.scrollBehavior;
  html.style.scrollBehavior = "auto";
  body.style.scrollBehavior = "auto";
  document.querySelectorAll(SCROLL_SELECTOR).forEach((el) => {
    el.style.scrollBehavior = "auto";
  });

  let cancelled = false;
  const start = performance.now();
  const restore = () => {
    html.style.scrollBehavior = prevHtml;
    body.style.scrollBehavior = prevBody;
    document.querySelectorAll(SCROLL_SELECTOR).forEach((el) => {
      el.style.scrollBehavior = "";
    });
    window.removeEventListener("wheel", onUserScroll, true);
    window.removeEventListener("touchstart", onUserScroll, true);
    window.removeEventListener("keydown", onUserScroll, true);
  };
  const onUserScroll = () => {
    restore();
    cancelled = true;
  };
  // Capture phase so we catch the event before anything else handles it.
  window.addEventListener("wheel", onUserScroll, { passive: true, capture: true });
  window.addEventListener("touchstart", onUserScroll, { passive: true, capture: true });
  window.addEventListener("keydown", onUserScroll, true);

  const tick = () => {
    if (cancelled) return;
    resetAllScrollPositions();
    if (performance.now() - start < durationMs) {
      requestAnimationFrame(tick);
    } else {
      restore();
    }
  };

  resetAllScrollPositions();
  requestAnimationFrame(tick);

  return () => {
    cancelled = true;
    restore();
  };
};
